// Checklist sidebar — canonical 2-group rollup (Transport + Admin) from
// `transactionChecklist.ts`. Reads tri-state `*_check` fields off the
// `transactions_with_checks` row and collapses `not_required`/null → "locked",
// `pending` → "pending", `completed` → "completed".
//
// Role-aware: the operator's role floats the matching group with a soft-blue
// header tint.
//
// Rows are clickable — click scrolls to the owning section anchor on the
// view page (anchors emitted in `transactionSections.tsx`).
// Pure render — no fetch.

import React from "react";
import { useTranslation } from "react-i18next";
import type { Transaction } from "@shared/types/transaction";
import { Checklist, type ChecklistRow } from "@ds/display/Checklist";
import { Pill } from "@ds/display/Pill";
import { Icon, type IconName } from "@ds/display/Icon";
import { Inline } from "@ds/layout/Inline";
import { TextValue } from "@ds/display/TextValue";
import { Stack } from "@ds/layout/Stack";
import { Box } from "@ds/layout/Box";
import { useCurrentEmployee } from "../../../contexts/useCurrentEmployee";
import { CHECK_GROUPS, type CheckGroup, type CheckKey } from "../transactionChecklist";

type RoleView = "logistics" | "sales" | "administration";
type RowState = "completed" | "pending" | "locked";

const GROUP_ICON: Record<CheckGroup["id"], IconName> = {
  transport: "truck",
  admin:     "fileText",
};

const GROUP_PRIMARY_ROLE: Record<CheckGroup["id"], "logistics" | "administration"> = {
  transport: "logistics",
  admin:     "administration",
};

function rowState(v: unknown): RowState {
  if (v === "completed") return "completed";
  if (v === "pending")   return "pending";
  return "locked"; // not_required / null
}

/** Map each check key to the section anchor that owns its action. */
const CHECK_TO_SECTION: Partial<Record<CheckKey, string>> = {
  transport_complete:           "transport",
  transport_order_generated:    "transport",
  transport_order_sent:         "transport",
  loading_notification_sent:    "documents",
  unloading_notification_sent:  "documents",
  unload_entry_request_sent:    "documents",
  unloading_confirmation_sent:  "documents",
  loading_photo_approved:       "documents",
  annex_copy_approved:          "documents",
  cmr_copy_approved:            "documents",
  weight_ticket_copy_approved:  "documents",
  annex_original_approved:      "documents",
  annex7_generated:             "documents",
  cmr_generated:                "documents",
  cmr_original_approved:        "documents",
  customs_docs_approved:        "documents",
  pending_docs_review:          "documents",
  unload_weight_set:            "basic_details",
  number_of_bales_set:          "basic_details",
  recycling_process_set:        "basic_details",
  load_price_set:               "pricing",
  unload_price_set:             "pricing",
  income_invoices_issued:       "faktury",
  cost_invoices_issued:         "faktury",
  load_invoices_issued:         "faktury",
  unload_invoices_issued:       "faktury",
  transport_invoices_issued:    "faktury",
};

function scrollToSection(key: CheckKey) {
  const target = CHECK_TO_SECTION[key];
  if (!target) return;
  document.getElementById(`section-${target}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function ClickableLabel({ checkKey, label, faint }: { checkKey: CheckKey; label: string; faint?: boolean }) {
  const target = CHECK_TO_SECTION[checkKey];
  if (!target) return <TextValue>{label}</TextValue>;
  return (
    <TextValue
      underline="dotted"
      tone={faint ? "faint" : "primary"}
      onClick={() => scrollToSection(checkKey)}
    >
      {label}
    </TextValue>
  );
}

function GroupBlock({ group, tx, t, role, isFirst }: {
  group: CheckGroup;
  tx: Transaction;
  t: ReturnType<typeof useTranslation>["t"];
  role: RoleView;
  isFirst: boolean;
}) {
  const rec = tx as unknown as Record<string, unknown>;
  const rows: ChecklistRow[] = group.keys
    .filter((k) => rec[`${k}_check`] != null && rec[`${k}_check`] !== "not_required")
    .map<ChecklistRow>((k: CheckKey) => {
      const state = rowState(rec[`${k}_check`]);
      const override = tx.check_overrides?.[k];
      return {
        key:   k,
        label: (
          <Inline as="span" inline gap="xs" align="center">
            <ClickableLabel checkKey={k} label={t(`transactions.checklist.${k}`, k)} faint={state === "locked"} />
            {override && (
              <TextValue
                as="span"
                testId={`checklist-override-marker-${k}`}
                title={`${t("transactions.checklist.override.manual_marker", "Oznaczone ręcznie")} · ${override.by ?? "—"} · ${new Date(override.at).toLocaleDateString("pl-PL")} · ${override.reason}`}
              >
                <Icon name="user" size="sm" tone="grey" />
              </TextValue>
            )}
          </Inline>
        ),
        state,
      };
    });
  if (rows.length === 0) return null;
  const done       = rows.filter((r) => r.state === "completed").length;
  const total      = rows.length;
  const primaryRole = GROUP_PRIMARY_ROLE[group.id];
  const isPrimary   = (role === "logistics"      && primaryRole === "logistics")
                   || (role === "administration" && primaryRole === "administration");
  return (
    <Box size="sm" tone={isPrimary ? "blue" : "neutral"} divider={!isFirst ? "top" : undefined}>
      <Stack gap="xs">
        <Inline align="center" justify="between">
          <Inline as="span" inline gap="xs" align="center">
            <Icon name={GROUP_ICON[group.id]} size="sm" tone="current" />
            <TextValue size="xs" weight="semibold" uppercase tone={isPrimary ? "blue" : "muted"}>
              {t(group.labelKey, group.id)}
            </TextValue>
          </Inline>
          <TextValue size="xs" tone={isPrimary ? "blue" : "muted"} tabular>
            {total === 0 ? t("transactions.checklist.not_applicable", "Nie dotyczy") : `${done}/${total}`}
          </TextValue>
        </Inline>
        <Checklist rows={rows} testId={`transaction-checklist-${group.id}`} />
      </Stack>
    </Box>
  );
}

function RoleHeader({ roleLabel }: { roleLabel: string }) {
  const { t } = useTranslation();
  return (
    <Box size="sm" tone="blue" divider="bottom">
      <Inline align="center" justify="between" gap="xs">
        <Stack gap="none">
          <TextValue as="div" size="xs" weight="semibold" uppercase tone="blue">
            {t("transactions.checklist.your_tasks", "Twoje zadania")}
          </TextValue>
          {/* Hint — tone="faint" replaces opacity-dimmed blue (closed enum). */}
          <TextValue as="div" size="xs" tone="faint">
            {t("transactions.checklist.your_tasks_hint", "Zadania {{roleLabel}} są wyróżnione poniżej", { roleLabel })}
          </TextValue>
        </Stack>
        <Icon name="lightning" size="sm" tone="current" />
      </Inline>
    </Box>
  );
}

/** Order CHECK_GROUPS so the user's primary-role group comes first. */
function sortGroups(groups: CheckGroup[], role: RoleView): CheckGroup[] {
  const score = (g: CheckGroup) => {
    if (role === "logistics") return GROUP_PRIMARY_ROLE[g.id] === "logistics" ? 0 : 1;
    if (role === "administration") return GROUP_PRIMARY_ROLE[g.id] === "administration" ? 0 : 1;
    return 0; // sales — keep declared order
  };
  return [...groups].sort((a, b) => score(a) - score(b));
}

export interface TransactionChecklistProps {
  transaction: Transaction;
}

export const TransactionChecklist: React.FC<TransactionChecklistProps> = ({ transaction: tx }) => {
  const { t } = useTranslation();
  const { hasRole } = useCurrentEmployee();
  const role: RoleView = hasRole("admin", "administration", "developer") ? "administration"
                       : hasRole("logistics")               ? "logistics"
                       :                                       "sales";
  const roleLabel = role === "administration" ? t("transactions.checklist.role.administration", "Administracji")
                  : role === "logistics"      ? t("transactions.checklist.role.logistics", "Logistyki")
                  :                             t("transactions.checklist.role.sales", "Sprzedaży");
  const ordered = sortGroups(CHECK_GROUPS, role);
  return (
    <>
      <Inline justify="end">
        <Pill tone="blue" size="sm">{t("transactions.checklist.your_tasks", "Twoje zadania")} · {roleLabel}</Pill>
      </Inline>
      <RoleHeader roleLabel={roleLabel} />

      {ordered.map((g, i) => (
        <GroupBlock key={g.id} group={g} tx={tx} t={t} role={role} isFirst={i === 0} />
      ))}
    </>
  );
};
